import i18n from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";
import { useLocaleStore } from "./locale";

const NAMESPACES = [
	"common",
	"nav",
	"products",
	"orders",
	"parties",
	"work-items",
	"settings",
	"admin",
	"filters",
	"notifications",
	"onboarding",
	"compliance",
	"metal",
	"plugins",
	"eval",
	"activity",
	"background-jobs",
	"categories",
	"pricing",
	"tasks",
	"knowledge",
	"assistant",
	"agent-fs",
	"docs",
	"widget",
	"portals",
	"commerce",
	"diagnostics",
	"manufacturing",
	"advisor",
] as const;

export type Namespace = (typeof NAMESPACES)[number];

// Lazy-load locale JSON files via Vite dynamic import
const localeModules = import.meta.glob("../locales/**/*.json");

function loadResource(
	lng: string,
	ns: string,
): Promise<Record<string, string>> {
	const key = `../locales/${lng}/${ns}.json`;
	const loader = localeModules[key];
	if (!loader) return Promise.resolve({});
	return loader().then(
		(mod) => (mod as { default: Record<string, string> }).default,
	);
}

i18n
	.use(ICU)
	.use(initReactI18next)
	.use({
		type: "backend" as const,
		read(
			language: string,
			namespace: string,
			callback: (
				err: Error | null,
				data: Record<string, string> | null,
			) => void,
		) {
			loadResource(language, namespace)
				.then((data) => callback(null, data))
				.catch((err) => callback(err, null));
		},
	})
	.init({
		lng: useLocaleStore.getState().language,
		fallbackLng: "ru",
		defaultNS: "common",
		ns: ["common", "nav"],
		interpolation: { escapeValue: false },
	});

// Sync language changes from the locale store to i18next
useLocaleStore.subscribe((state) => {
	if (i18n.language !== state.language) {
		i18n.changeLanguage(state.language);
	}
});

// Sync workspace locale → locale store when workspace changes
import { useWorkspaceStore } from "./workspace";

useWorkspaceStore.subscribe((state) => {
	const ws = state.workspaces.find((w) => w.id === state.selectedId);
	if (ws) {
		useLocaleStore
			.getState()
			.syncFromWorkspace(
				ws.settings.locale as string | undefined,
				ws.settings.timezone as string | undefined,
			);
	}
});

export default i18n;
